import {
  extractClosestEdge,
  type Edge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import {
  monitorForElements,
  type ElementDragPayload,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { type DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/types";
import { useEffect, useEffectEvent, useState } from "react";

import { ALTERNATE_DRAG_TYPE } from "./useAlternateTabDragAndDrop";

interface AlternateDragAndDropInput {
  dndInstanceId: string;
  getAlternateIds: () => string[];
  onReorder: (alternateIds: string[]) => void;
}

// A drop slot is the gap before the alternate at that index; the slot after
// the last alternate equals the alternates count. The slots on both sides of
// the dragged tab are left out, since dropping there changes nothing.
function getDropSlot(
  location: DragLocationHistory,
  source: ElementDragPayload,
): number | null {
  const target = location.current.dropTargets[0];
  const startIndex = source.data.index;
  const targetIndex = target?.data.index;

  if (typeof startIndex !== "number" || typeof targetIndex !== "number") {
    return null;
  }

  const slot =
    extractClosestEdge(target.data) === "right" ? targetIndex + 1 : targetIndex;

  return slot === startIndex || slot === startIndex + 1 ? null : slot;
}

export function getDropIndicatorEdge(
  dropSlot: number | null,
  index: number,
  count: number,
): Edge | null {
  if (dropSlot === index) {
    return "left";
  }

  return dropSlot === count && index === count - 1 ? "right" : null;
}

export function useAlternateDragAndDrop(
  input: AlternateDragAndDropInput,
): number | null {
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  const getAlternateIds = useEffectEvent(input.getAlternateIds);
  const onReorder = input.onReorder;

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => {
        return (
          source.data.type === ALTERNATE_DRAG_TYPE &&
          source.data.instanceId === input.dndInstanceId
        );
      },
      onDrag: ({ location, source }) => {
        setDropSlot(getDropSlot(location, source));
      },
      onDrop: ({ location, source }) => {
        setDropSlot(null);

        const destination = location.current.dropTargets[0];
        const startIndex = source.data.index;
        const indexOfTarget = destination?.data.index;

        if (
          typeof startIndex !== "number" ||
          typeof indexOfTarget !== "number"
        ) {
          return;
        }

        const destinationIndex = getReorderDestinationIndex({
          startIndex,
          indexOfTarget,
          closestEdgeOfTarget: extractClosestEdge(destination.data),
          axis: "horizontal",
        });

        if (destinationIndex === startIndex) {
          return;
        }

        // Read the ids at drop time; they always hold the current alternates.
        const alternateIds = getAlternateIds();
        const [movedId] = alternateIds.splice(startIndex, 1);
        alternateIds.splice(destinationIndex, 0, movedId);
        onReorder(alternateIds);
      },
    });
  }, [input.dndInstanceId, onReorder]);

  return dropSlot;
}
